import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Slide,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TransitionProps } from "@mui/material/transitions";
import Button from "../../common/Button";
import { tokens } from "../../../theme";

interface RoleConfirmationPopupProps {
  open: boolean;
  onClose: () => void;
  onConfirmMedicalRole: () => void;
  onConfirmOtherRole: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const RoleConfirmationPopup: React.FC<RoleConfirmationPopupProps> = ({
  open,
  onClose,
  onConfirmMedicalRole,
  onConfirmOtherRole,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      onClose={onClose}
      keepMounted
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: isDark
            ? "0 20px 60px rgba(0,0,0,0.6)"
            : "0 20px 60px rgba(0,0,0,0.15)",
          backdropFilter: "blur(12px)",
        },
      }}
    >
      <DialogContent
        sx={{
          position: "relative",
          px: { xs: 3, sm: 4 },
          py: { xs: 3.5, sm: 4 },
          background: isDark
            ? "linear-gradient(135deg, #0f2d26 0%, #141c22 100%)"
            : "linear-gradient(135deg, #e8fffb 0%, #f6fffd 100%)",
        }}
      >
        {/* Close Icon */}
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: colors.grey[300],
            backgroundColor: isDark
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.04)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.12)"
                : "rgba(0,0,0,0.08)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Title */}
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5 }}>
          Confirm your role
        </Typography>

        {/* Body */}
        <Typography
          sx={{
            fontSize: "15px",
            lineHeight: 1.7,
            color: colors.grey[300],
            mb: 2.5,
          }}
        >
          To create an account here, you must be an{" "}
          <Box component="span" sx={{ fontWeight: 600 }}>
            Owner, Provider, or Staff member
          </Box>{" "}
          of a Medical Practice or Medical Facility.
        </Typography>

        <Typography
          sx={{
            fontSize: "14px",
            lineHeight: 1.7,
            color: colors.grey[400],
            mb: 4,
          }}
        >
          Other industry roles (Reps, Agents, Distributors, RCM/Billing,
          Manufacturers, etc.) follow a different workflow to register.
        </Typography>

        {/* Actions */}
        <Box display="flex" flexDirection="column" gap={1.75}>
          <Button
            fullWidth
            variant="contained"
            onClick={onConfirmMedicalRole}
            sx={{
              py: 1.6,
              fontSize: "0.95rem",
              fontWeight: 700,
              borderRadius: 12,
              background:
                "linear-gradient(90deg, #19C1A3, #14B8B5, #19A9FF)",
              textTransform: "none",
              "&:hover": {
                filter: "brightness(1.05)",
                boxShadow: "0 8px 20px rgba(25,193,163,0.35)",
              },
            }}
          >
            Yes, I am part of a Medical Practice
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={onConfirmOtherRole}
            sx={{
              py: 1.4,
              fontSize: "0.9rem",
              fontWeight: 600,
              borderRadius: 12,
              textTransform: "none",
              borderColor: colors.grey[600],
              color: colors.grey[300],
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(0,0,0,0.04)",
              },
            }}
          >
            Other role (Rep, RCM, Manufacturer, etc.)
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RoleConfirmationPopup;
